<template>
    <!--
      Teleport to body so `position: fixed` resolves against the viewport. The route
      component on mobile has a `motion-blur-in-*` filter that creates a CSS containing
      block, which would otherwise trap the modal inside the animated route (HAP-1068).
    -->
    <Teleport to="body">
    <div
        ref="modal"
        @click.prevent.stop="close"
        tabindex="-1"
        class="
            fixed w-full h-screen bg-gray-800/50 dark:bg-black/60 backdrop-blur-sm md:backdrop-blur-md opacity-0 transition-all duration-500
            flex justify-center z-[10000] inset-0 !m-0 overscroll-none cursor-default
            py-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]
        "
        :class="[fadeIn ? '!opacity-100' : '', alignClasses]"
        aria-modal="true"
        v-show="active"
        role="dialog"
    >
        <!--
          Close affordance (HAP-3974). Two things were wrong with the bare glyph
          this replaces: it inherited the ambient text colour, so on a dark
          backdrop it was near-invisible; and with no z-index it lost the paint
          order to the `relative` content box below, so on any wide dialog it was
          literally behind the card. Now it is a self-contained chip — white on a
          dark scrim with a hairline ring, legible over artwork or either theme —
          sized to a 44px touch target.
        -->
        <button
            @click.prevent.stop="close"
            aria-label="Close"
            class="absolute z-10 top-[max(0.5rem,env(safe-area-inset-top))] right-[max(0.75rem,env(safe-area-inset-right))]
                w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full
                bg-black/55 text-white ring-1 ring-white/30 shadow-lg backdrop-blur-sm
                transition-colors hover:bg-black/75
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
        ><i class="fa fa-times text-lg md:text-2xl" /></button>
        <div
            ref="modalContent"
            @click.stop
            class="shadow-card w-full p-4 md:p-8 dark:bg-gray-800 bg-white relative
            border dark:border-gray-700 space-y-4 md:space-y-8"
            :class="[dialogClasses, maxWidth, contentShapeClasses, allowOverflow ? 'overflow-visible' : 'overflow-y-scroll overscroll-contain [-webkit-overflow-scrolling:touch]']"
        >
            <!--
              Drag-to-dismiss grab zone — mobile bottom-sheet only. `touch-action:none`
              guarantees the browser hands us the pointer stream (instead of taking it
              for a scroll), so the gesture is unambiguous. Negative margins let it span
              the sheet's full width flush to the top edge.
            -->
            <div
                v-if="bottomSheet"
                @pointerdown="onPointerDown"
                class="sm:hidden -mx-4 -mt-4 mb-1 pt-3 pb-2 flex justify-center cursor-grab [touch-action:none]"
                aria-hidden="true"
            >
                <div class="h-1.5 w-10 rounded-full bg-gray-300 dark:bg-gray-600" />
            </div>
            <slot :close="close"/>
        </div>
    </div>
    </Teleport>
</template>

<script>
/*
  Modal / bottom-sheet (HAP-1892, parent HAP-1884 Phase 3.2).

  - Default presentation: a springy scale+fade entrance (was fade-only), driven
    by the Web Animations API using the shared P0.2 motion tokens (motion.js),
    matching the v-press / v-img-fade / v-reveal language.
  - `bottomSheet` prop: on mobile (< sm) the dialog docks to the bottom edge,
    slides up, and can be dragged down to dismiss (native iOS-sheet feel). At
    sm+ it falls back to the centered card — sheets are a phone affordance.
  - Body-teleport + z-[10000] are preserved (memories cfl_motion_filter_modal_gotcha,
    cfl_overlay_zindex_announcement_bar). Safe-area insets + reduced-motion honored.

  No new dependency: @vueuse/motion / motion-v were not needed — pointer events +
  WAAPI give us fully interruptible drag-to-dismiss with the same tokens the rest
  of the system already uses, so we stay consistent and avoid the extra weight.
*/
import { DURATION, EASING } from '../../helpers/motion'

// Live reduced-motion check — JS-driven motion must neutralize itself the same
// way the global CSS guard does (memory: HAP-1886).
const reducedMotionMQ =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : { matches: false }

// `sm` breakpoint (640px) is the mobile/desktop boundary — the bottom-sheet
// presentation and its drag-to-dismiss only apply below it.
const isMobileViewport = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(max-width: 639px)').matches
        : false

// Drag distance (px) past which a release dismisses instead of springing back.
const DISMISS_DISTANCE = 110
const DRAG_START_SLOP = 6

export default {
    props: {
        dialogClasses: {type: String, default: ''},
        maxWidth: {default: 'max-w-md'},
        active: {default: true},
        allowOverflow: {type: Boolean, default: false},
        // Opt into the mobile bottom-sheet presentation with drag-to-dismiss.
        bottomSheet: {type: Boolean, default: false},
    },
    data() {
        return {
            fadeIn: false,
            // drag state
            dragId: null,
            dragStartY: 0,
            dragging: false,
            dragCandidate: false,
        }
    },
    computed: {
        // Bottom sheet docks to the bottom on mobile; everything else is centered.
        alignClasses() {
            return this.bottomSheet ? 'items-end sm:items-center' : 'items-start sm:items-center'
        },
        // Mobile sheet: square the bottom, round only the top, hug the edges, give
        // it room + a safe-area bottom pad. At sm+ revert to the normal card.
        contentShapeClasses() {
            if (this.bottomSheet) {
                return '!m-0 sm:!m-2 rounded-t-2xl rounded-b-none sm:rounded ' +
                    'max-h-[88vh] sm:max-h-[75vh] pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-8'
            }
            return 'rounded m-2 max-h-[75vh]'
        },
    },
    watch: {
        active: {
            handler() {
                if (this.active) this.freeze()
                else this.thaw()
            },
            immediate: true
        }
    },
    beforeUnmount() {
        this.thaw();
    },
    unmounted() {
        this.thaw();
    },
    methods: {
        freeze() {
            this.$nextTick(() => {
                this.$refs?.modalContent?.focus()
                this.fadeIn = true
                document.body.style.overflow = 'hidden';
                document.getElementById('app').setAttribute('aria-hidden', 'true');
                this.$refs.modal.setAttribute('aria-modal', 'true');
                this.attachListeners();
                this.animateIn();
            });
        },
        thaw() {
            document.body.style.overflow = '';
            document.getElementById('app').setAttribute('aria-hidden', 'false');
            this.$refs?.modal?.removeAttribute('aria-modal');
            this.removeListeners();
        },
        // Spring/scale (or slide-up, for the sheet) entrance via WAAPI.
        animateIn() {
            const el = this.$refs?.modalContent
            if (!el) return
            if (reducedMotionMQ.matches) return // CSS backdrop fade only; no transform
            const sheet = this.bottomSheet && isMobileViewport()
            const frames = sheet
                ? [{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }]
                : [{ opacity: 0, transform: 'scale(0.94) translateY(6px)' }, { opacity: 1, transform: 'scale(1) translateY(0)' }]
            el.animate(frames, {
                duration: sheet ? DURATION.base : DURATION.base,
                easing: sheet ? EASING.decel : EASING.spring,
                fill: 'both',
            })
        },
        close() {
            if (this.fadeIn === false) return // already closing
            this.fadeIn = false
            this.thaw()
            this.animateOut()
            setTimeout(() => {
                this.$emit('hide')
            }, 500);
        },
        // Slide/scale the content out so it doesn't just hard-cut while the
        // backdrop fades (CSS, 500ms). Both finish well before the emit above.
        animateOut(fromY = 0) {
            const el = this.$refs?.modalContent
            if (!el) return
            if (reducedMotionMQ.matches) return
            const sheet = this.bottomSheet && isMobileViewport()
            const frames = sheet
                ? [{ transform: `translateY(${fromY}px)` }, { transform: 'translateY(100%)' }]
                : [{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(0.94)' }]
            el.animate(frames, {
                duration: DURATION.base,
                easing: sheet ? EASING.accel : EASING.standard,
                fill: 'both',
            })
        },

        /* ---- Drag-to-dismiss (mobile bottom sheet only) ---------------------- */
        onPointerDown(e) {
            if (!this.bottomSheet || !isMobileViewport()) return
            if (e.pointerType === 'mouse' && e.button !== 0) return
            // Candidate only — we don't commit to a drag until the finger moves
            // down far enough AND the content is scrolled to the top (so inner
            // scrolling still works normally).
            this.dragId = e.pointerId
            this.dragStartY = e.clientY
            this.dragging = false
            this.dragCandidate = true
            window.addEventListener('pointermove', this.onPointerMove, { passive: false })
            window.addEventListener('pointerup', this.onPointerUp, { passive: true })
            window.addEventListener('pointercancel', this.onPointerUp, { passive: true })
        },
        onPointerMove(e) {
            if (!this.dragCandidate || e.pointerId !== this.dragId) return
            const el = this.$refs?.modalContent
            if (!el) return
            const dy = e.clientY - this.dragStartY
            if (!this.dragging) {
                // Only a downward pull dismisses; an upward move ends the gesture.
                if (dy < -DRAG_START_SLOP) { this.dragCandidate = false; return }
                if (dy > DRAG_START_SLOP) {
                    this.dragging = true
                    el.style.transition = 'none'
                }
            }
            if (this.dragging) {
                e.preventDefault() // stop the content from scrolling under us
                const offset = Math.max(0, dy)
                el.style.transform = `translateY(${offset}px)`
                // Fade the backdrop with the drag for tactile feedback.
                const h = el.offsetHeight || 1
                const progress = Math.min(1, offset / h)
                this.$refs.modal.style.opacity = String(1 - progress * 0.6)
            }
        },
        onPointerUp(e) {
            window.removeEventListener('pointermove', this.onPointerMove)
            window.removeEventListener('pointerup', this.onPointerUp)
            window.removeEventListener('pointercancel', this.onPointerUp)
            const el = this.$refs?.modalContent
            const wasDragging = this.dragging
            this.dragCandidate = false
            this.dragging = false
            if (!el || !wasDragging) return
            const dy = Math.max(0, e.clientY - this.dragStartY)
            if (dy > DISMISS_DISTANCE) {
                this.dismissByDrag(dy)
            } else {
                this.springBack(el)
            }
        },
        // Continue the downward motion from where the finger left off, then close.
        dismissByDrag(fromY) {
            const el = this.$refs.modalContent
            el.style.transform = ''
            el.style.transition = ''
            this.$refs.modal.style.opacity = ''
            this.fadeIn = false
            this.thaw()
            this.animateOut(fromY)
            setTimeout(() => this.$emit('hide'), 500)
        },
        // Snap the sheet back to rest with a spring (or instantly under reduced motion).
        springBack(el) {
            this.$refs.modal.style.opacity = ''
            const restore = () => { el.style.transform = ''; el.style.transition = '' }
            if (reducedMotionMQ.matches) { restore(); return }
            const from = el.style.transform || 'translateY(0px)'
            el.style.transform = ''
            el.style.transition = ''
            el.animate(
                [{ transform: from }, { transform: 'translateY(0px)' }],
                { duration: DURATION.base, easing: EASING.spring },
            )
        },

        attachListeners() {
            window.addEventListener('keydown', this.handleKeydown);
        },
        removeListeners() {
            window.removeEventListener('keydown', this.handleKeydown);
            window.removeEventListener('pointermove', this.onPointerMove);
            window.removeEventListener('pointerup', this.onPointerUp);
            window.removeEventListener('pointercancel', this.onPointerUp);
        },
        handleKeydown(e) {
            if (e.key === 'Tab') {
                this.manageFocus(e);
            } else if (e.key === 'Escape') {
                this.close()
            }
        },
        manageFocus(e) {
            let focusable = Array.from(this.$refs.modal.querySelectorAll('button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'));
            focusable = focusable.filter(el => window.getComputedStyle(el).display !== 'none');

            if (!focusable.includes(document.activeElement)) {
                e.preventDefault();
                focusable[0].focus();
            } else if (e.shiftKey && document.activeElement === focusable[0]) {
                e.preventDefault();
                focusable[focusable.length - 1].focus();
            } else if (document.activeElement === focusable[focusable.length - 1]) {
                e.preventDefault();
                focusable[0].focus();
            }

        }
    }
}
</script>
